import { clone, joinLines } from './rapidPageUtils.js';

export const GLOBAL_STATE_KEY = 'dha_prime__rapid_v2_global';
export const DETAIL_STATE_PREFIX = 'dha_prime__rapid_v2_detail_';
export const TOUR_STATE_KEY = 'dha_prime__rapid_v2_tour';

function safeParse(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function readStorage(key) {
  if (typeof window === 'undefined') return null;
  return safeParse(window.localStorage.getItem(key));
}

function writeStorage(key, value) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(value));
}

function removeStorage(key) {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(key);
}

export function readTourState() {
  if (typeof window === 'undefined') return { open: false, step: 1 };
  return safeParse(window.sessionStorage.getItem(TOUR_STATE_KEY)) || { open: false, step: 1 };
}

export function writeTourState(value) {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(TOUR_STATE_KEY, JSON.stringify(value));
}

export function clearTourState() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(TOUR_STATE_KEY);
}

export function clearStaleRapidState(seedVersion) {
  if (typeof window === 'undefined' || !seedVersion) return;
  const keys = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key && (key === GLOBAL_STATE_KEY || key.startsWith(DETAIL_STATE_PREFIX))) keys.push(key);
  }
  for (const key of keys) {
    const parsed = safeParse(window.localStorage.getItem(key));
    if (parsed?.seedVersion && parsed.seedVersion !== seedVersion) {
      window.localStorage.removeItem(key);
    }
  }
}

function globalDefaults(seedVersion) {
  return {
    seedVersion,
    roleLens: 'Executive Sponsor',
    modified: false,
  };
}

function toComparableGlobal(state) {
  return { roleLens: state.roleLens || 'Executive Sponsor' };
}

export function persistGlobalState(seedVersion, nextState) {
  const merged = {
    ...globalDefaults(seedVersion),
    ...nextState,
    seedVersion,
  };
  merged.modified =
    JSON.stringify(toComparableGlobal(merged)) !==
    JSON.stringify(toComparableGlobal(globalDefaults(seedVersion)));
  writeStorage(GLOBAL_STATE_KEY, merged);
  return merged;
}

export function readGlobalState(seedVersion) {
  clearStaleRapidState(seedVersion);
  const stored = readStorage(GLOBAL_STATE_KEY);
  if (!stored || stored.seedVersion !== seedVersion) return persistGlobalState(seedVersion, {});
  return persistGlobalState(seedVersion, stored);
}

function detailStateKey(rapidId) {
  return `${DETAIL_STATE_PREFIX}${rapidId}`;
}

function buildFieldReviewMap(fields = {}) {
  return Object.fromEntries(Object.keys(fields).map((key) => [key, 'Accepted']));
}

function buildDefaultDetailState(rapid) {
  const intakeFields = clone(rapid.expectedExtraction || {});
  return {
    seedVersion: rapid.seedVersion,
    modified: false,
    intake: {
      mode: 'seeded',
      acknowledgedExperimental: false,
      liveFailure: false,
      extractionStatus: 'Accepted',
      fields: intakeFields,
      fieldReview: buildFieldReviewMap(intakeFields),
      customText: '',
      customEcho: '',
    },
    canvas: {
      mode: 'Edit',
      problemStatement: rapid.canvas?.problemStatement || '',
      northStar: rapid.canvas?.northStar || '',
      assumption: rapid.canvas?.assumptions?.[0] || '',
      milestone: rapid.canvas?.versionHistory?.[0]?.note || '',
      scopeInText: joinLines(rapid.canvas?.scopeIn || []),
      scopeOutText: joinLines(rapid.canvas?.scopeOut || []),
      history: clone(rapid.canvas?.versionHistory || []),
    },
    conversions: [],
    createdActions: [],
    createdRfIs: [],
    createdDecisions: [],
    createdAssumptions: [],
    createdBarriers: [],
    createdDataIssues: [],
    closeout: {
      validationStatus: rapid.closeout?.sponsorValidation || 'Pending',
      handoffOwner: rapid.closeout?.handoffOwner || '',
      routedDestination: 'Standard functional work queue',
      checklist: clone(rapid.closeout?.closeoutReadiness?.checklist || []),
    },
    createdBriefPreview: false,
    activityEntries: [],
  };
}

function toComparableDetail(state) {
  return {
    intake: state.intake,
    canvas: state.canvas,
    conversions: state.conversions,
    createdActions: state.createdActions,
    createdRfIs: state.createdRfIs,
    createdDecisions: state.createdDecisions,
    createdAssumptions: state.createdAssumptions,
    createdBarriers: state.createdBarriers,
    createdDataIssues: state.createdDataIssues,
    closeout: state.closeout,
    createdBriefPreview: state.createdBriefPreview,
    activityEntries: state.activityEntries,
  };
}

export function persistDetailState(rapid, nextState) {
  const base = buildDefaultDetailState(rapid);
  const merged = {
    ...base,
    ...nextState,
    intake: {
      ...base.intake,
      ...nextState.intake,
      fields: {
        ...base.intake.fields,
        ...(nextState.intake?.fields || {}),
      },
      fieldReview: {
        ...base.intake.fieldReview,
        ...(nextState.intake?.fieldReview || {}),
      },
    },
    canvas: {
      ...base.canvas,
      ...(nextState.canvas || {}),
    },
    closeout: {
      ...base.closeout,
      ...(nextState.closeout || {}),
    },
    conversions: Array.isArray(nextState.conversions) ? nextState.conversions : base.conversions,
    createdActions: Array.isArray(nextState.createdActions)
      ? nextState.createdActions
      : base.createdActions,
    createdRfIs: Array.isArray(nextState.createdRfIs) ? nextState.createdRfIs : base.createdRfIs,
    createdDecisions: Array.isArray(nextState.createdDecisions)
      ? nextState.createdDecisions
      : base.createdDecisions,
    createdAssumptions: Array.isArray(nextState.createdAssumptions)
      ? nextState.createdAssumptions
      : base.createdAssumptions,
    createdBarriers: Array.isArray(nextState.createdBarriers)
      ? nextState.createdBarriers
      : base.createdBarriers,
    createdDataIssues: Array.isArray(nextState.createdDataIssues)
      ? nextState.createdDataIssues
      : base.createdDataIssues,
    activityEntries: Array.isArray(nextState.activityEntries)
      ? nextState.activityEntries
      : base.activityEntries,
    seedVersion: rapid.seedVersion,
  };
  merged.modified =
    JSON.stringify(toComparableDetail(merged)) !== JSON.stringify(toComparableDetail(base));
  writeStorage(detailStateKey(rapid.id), merged);
  return merged;
}

// AC-RAPID-4: server-persisted review state (rapid.reviewState) is the source
// of truth. localStorage is only a per-browser cache seeded from the server, so
// a different browser/user sees the same accept/edit/reject decisions.
function usableServerReviewState(rapid, serverReviewState) {
  if (!serverReviewState || typeof serverReviewState !== 'object') return null;
  if (Array.isArray(serverReviewState)) return null;
  if (serverReviewState.seedVersion && serverReviewState.seedVersion !== rapid.seedVersion) {
    return null;
  }
  return serverReviewState;
}

export function readDetailState(rapid, serverReviewState = null) {
  clearStaleRapidState(rapid.seedVersion);
  const server = usableServerReviewState(rapid, serverReviewState);
  if (server) return persistDetailState(rapid, server);
  const stored = readStorage(detailStateKey(rapid.id));
  if (!stored || stored.seedVersion !== rapid.seedVersion) return persistDetailState(rapid, {});
  return persistDetailState(rapid, stored);
}

// The payload synced to POST /rapids/:id/review-state — the human-reviewable
// sections only (no seedVersion/modified bookkeeping; the server stamps
// reviewer + timestamp).
export function toReviewStatePayload(state) {
  return {
    intake: state.intake,
    canvas: state.canvas,
    conversions: state.conversions,
    createdActions: state.createdActions,
    createdRfIs: state.createdRfIs,
    createdDecisions: state.createdDecisions,
    createdAssumptions: state.createdAssumptions,
    createdBarriers: state.createdBarriers,
    createdDataIssues: state.createdDataIssues,
    closeout: state.closeout,
    createdBriefPreview: state.createdBriefPreview,
    activityEntries: state.activityEntries,
  };
}

export function clearRapidBrowserState() {
  removeStorage(GLOBAL_STATE_KEY);
  if (typeof window !== 'undefined') {
    const keys = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key && key.startsWith(DETAIL_STATE_PREFIX)) keys.push(key);
    }
    for (const key of keys) {
      window.localStorage.removeItem(key);
    }
  }
  clearTourState();
}
